import { TableIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { useGetDropRateAnalysis } from '~entities/classes/api/drop-rate-analysis';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~shared/shadcn/ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';

interface DropRateChartProps {
  sessionId: string;
}

export function DropRateChart({ sessionId }: DropRateChartProps) {
  const [showTable, setShowTable] = useState(false);
  const [grouping, setGrouping] = useState<'className' | 'groupName'>(
    'className',
  );

  const { data, isLoading } = useGetDropRateAnalysis({
    groupBy: grouping,
    sessionId,
  });

  const dropRateData = useMemo(
    () =>
      data
        ?.sort((a, b) => b.dropRatePercentage - a.dropRatePercentage)
        .slice(0, 10),
    [data],
  );

  return (
    <Card className='col-span-1'>
      <CardHeader className='flex flex-col space-y-2 sm:flex-row sm:items-start sm:justify-between sm:space-y-0'>
        <div>
          <CardTitle>Drop Rate Overview</CardTitle>
          <CardDescription>
            Classes with the lowest retention (%)
          </CardDescription>
        </div>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
          <Select
            value={grouping}
            onValueChange={(value: 'className' | 'groupName') => {
              setGrouping(value);
            }}
          >
            <SelectTrigger className='w-full sm:w-[180px]'>
              <SelectValue placeholder='Select grouping' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='className'>By Class Name</SelectItem>
              <SelectItem value='groupName'>By Group Name</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant='outline'
            size='icon'
            onClick={() => {
              setShowTable(!showTable);
            }}
          >
            <TableIcon className='size-4' />
          </Button>
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-hidden'>
        {isLoading ? (
          <div className='flex h-[200px] items-center justify-center'>
            Loading...
          </div>
        ) : !showTable ? (
          <ChartContainer
            config={{
              dropRatePercentage: {
                label: 'Drop Rate (%)',
                color: 'hsl(var(--chart-4))',
              },
            }}
            className='h-[300px] w-full overflow-hidden sm:h-[500px]'
          >
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={dropRateData ?? []}
                margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                barSize={30}
              >
                <XAxis
                  dataKey='name'
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 10, right: 10 }}
                  angle={-45}
                  textAnchor='end'
                  height={60}
                  interval={0}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  padding={{ top: 10 }}
                  tick={{ fontSize: 12 }}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  cursor={{ fill: 'transparent' }}
                />
                <Bar
                  dataKey='dropRatePercentage'
                  fill='hsl(var(--chart-4))'
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Class</TableHead>
                <TableHead>Initial Students</TableHead>
                <TableHead>Current Students</TableHead>
                <TableHead>Drop Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dropRateData
                ?.sort((a, b) => b.dropRatePercentage - a.dropRatePercentage)
                .map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className='font-medium'>{row.name}</TableCell>
                    <TableCell>{row.totalStudents}</TableCell>
                    <TableCell>
                      {row.totalStudents - row.droppedCount}
                    </TableCell>
                    <TableCell>{row.dropRatePercentage}%</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
